import { BigNumber } from '@ethersproject/bignumber';
import { Contract } from '@ethersproject/contracts';
import {
  delegationRegistryAbiV1,
  delegationRegistryAbiV2,
} from '@/contracts/abis/DelegationRegistryABI';
import BaseContract from '@/contracts/baseContract';

export enum DelegationType {
  NONE,
  ALL,
  CONTRACT,
  TOKEN,
}

export interface IDelegate {
  contract_: string;
  delegate: string;
  tokenId: BigNumber;
  type_: DelegationType;
  vault: string;
}

export default class DelegationRegistryContract extends BaseContract {
  private delegationContractAddressV1: string;
  private checkV1: boolean;
  private delegationContractAddressV2: string;
  private checkV2: boolean;

  constructor(
    networkId: number,
    delegationContractAddressV1: string,
    checkV1: boolean,
    delegationContractAddressV2: string,
    checkV2: boolean,
  ) {
    super(networkId);
    this.delegationContractAddressV1 = delegationContractAddressV1;
    this.delegationContractAddressV2 = delegationContractAddressV2;
    this.checkV1 = checkV1;
    this.checkV2 = checkV2;
  }

  protected _getContractInstanceV1(): Contract {
    return this._getContractInstance(
      this.delegationContractAddressV1,
      delegationRegistryAbiV1,
      false,
      false,
      false,
    );
  }

  protected _getContractInstanceV2(): Contract {
    return this._getContractInstance(
      this.delegationContractAddressV2,
      delegationRegistryAbiV2,
      false,
      false,
      false,
    );
  }

  async getDelegatesForContract(vault: string, contractAddress: string): Promise<string[]> {
    let delegatesV1: string[] = [];
    let delegatesV2: string[] = [];
    if (this.checkV1) {
      delegatesV1 = await this._callWeb3WithServerFallback(
        this.delegationContractAddressV1,
        delegationRegistryAbiV1,
        'getDelegatesForContract',
        [vault, contractAddress],
        false,
      );
    }
    if (this.checkV2) {
      const rawDelegatesV2 = await this._callWeb3WithServerFallback(
        this.delegationContractAddressV2,
        delegationRegistryAbiV2,
        'getOutgoingDelegations',
        [vault],
        false,
      );
      delegatesV2 = rawDelegatesV2
        .filter(
          (delegate: { type_: number; contract_: string; to: string }) =>
            delegate.type_ === 2 &&
            delegate.contract_.toLowerCase() === contractAddress.toLowerCase(),
        )
        .map((delegate: { to: string }) => {
          return delegate.to;
        });
    }
    return delegatesV1.concat(delegatesV2);
  }

  async getDelegatesForAll(vault: string): Promise<string[]> {
    let delegatesV1: string[] = [];
    let delegatesV2: string[] = [];
    if (this.checkV1) {
      delegatesV1 = await await this._callWeb3WithServerFallback(
        this.delegationContractAddressV1,
        delegationRegistryAbiV1,
        'getDelegatesForAll',
        [vault],
        false,
      );
    }
    if (this.checkV2) {
      const rawDelegatesV2 = await this._callWeb3WithServerFallback(
        this.delegationContractAddressV2,
        delegationRegistryAbiV2,
        'getOutgoingDelegations',
        [vault],
        false,
      );
      delegatesV2 = rawDelegatesV2
        .filter((delegate: { type: number; to: string }) => delegate.type === 1)
        .map((delegate: { to: string }) => {
          return delegate.to;
        });
    }
    return delegatesV1.concat(delegatesV2);
  }

  async getDelegationsByDelegate(delegate: string): Promise<IDelegate[]> {
    let delegationsV1: IDelegate[] = [];
    let delegationsV2: IDelegate[] = [];
    if (this.checkV1) {
      // our bridge provider *always* has this white listed on *every* network
      delegationsV1 = await this._callWeb3WithServerFallback(
        this.delegationContractAddressV1,
        delegationRegistryAbiV1,
        'getDelegationsByDelegate',
        [delegate],
        false,
      );
    }

    if (this.checkV2) {
      const rawDelegationsV2: {
        type_: number;
        from: string;
        to: string;
        contract_: string;
        tokenId: BigNumber;
      }[] = await this._callWeb3WithServerFallback(
        this.delegationContractAddressV2,
        delegationRegistryAbiV2,
        'getIncomingDelegations',
        [delegate],
        false,
      );
      delegationsV2 = rawDelegationsV2.map((delegation) => {
        return {
          type_: delegation.type_,
          vault: delegation.from,
          delegate: delegation.to,
          contract_: delegation.contract_,
          tokenId: delegation.tokenId,
        } as IDelegate;
      });
    }
    return delegationsV1.concat(delegationsV2);
  }
}
